// src/services/tasksService.js
import {auth,db} from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  serverTimestamp
} from "firebase/firestore";

const tasksCol=collection(db,"tasks");

/**
 * Internal helper: apply completedAt logic
 * Equivalent to your Mongoose pre('save') hook.
 */
function applyCompletedAtLogic(existingTask,updates){
  const merged={ ...existingTask, ...updates };

  if("completed" in updates){
    if(updates.completed===true){
      merged.completedAt=updates.completedAt ?? new Date();
    }else if(updates.completed===false){
      merged.completedAt=null;
    }
  }

  return merged;
}

/**
 * Get all tasks for the logged-in user
 * (replaces GET /api/tasks)
 */
export async function getTasks() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  // 🌟 Query only for the current user's personal tasks
  const q = query(
    tasksCol,
    where("userId", "==", user.uid),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);

  return snap.docs.map(d => {
    const data = d.data();
    
    // 🌟 Robust helper to safely convert dates
    const safeDate = (field) => {
      if (!field) return null;
      if (typeof field.toDate === 'function') return field.toDate(); // Firestore Timestamp
      return new Date(field); // ISO String
    };

    return {
      id: d.id,
      ...data,
      createdAt: safeDate(data.createdAt),
      updatedAt: safeDate(data.updatedAt),
      completedAt: safeDate(data.completedAt),
    };
  });
}
/**
 * Create a new task
 * (replaces POST /api/tasks)
 */
export async function createTask(data){
  const user=auth.currentUser;
  if(!user) throw new Error("Not authenticated");
  if(!data.title) throw new Error("Title is required");

  // base task
  let newTask = {
    title: data.title.trim(),
    notes: data.notes?.trim() || "", // 🌟 Save notes to DB
    difficulty: data.difficulty || "", // 🌟 Save difficulty tag
    completed: data.completed ?? false,
    importance: data.importance ?? 5,
    dueDate: data.dueDate ?? null,
    completedAt: null,
    userId: user.uid,
    group: data.group ?? "",
    recurrence: data.recurrence || {
        type: "none",
        days: [],
        date: null
    },
    isRecurringOriginal: data.recurrence?.type !== "none",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  // completedAt logic
  newTask=applyCompletedAtLogic({},newTask);

  const docRef=await addDoc(tasksCol,newTask);
  const snap=await getDoc(docRef);

  return {
    id:docRef.id,
    ...snap.data()
  };
}

/**
 * Update an existing task
 * (replaces PUT /api/tasks/:id)
 */
export async function updateTask(id,updates){
  const user=auth.currentUser;
  if(!user) throw new Error("Not authenticated");

  const ref=doc(db,"tasks",id);
  const snap=await getDoc(ref);

  if(!snap.exists()){
    throw new Error("Task not found or you are not authorized to edit it.");
  }

  const existing=snap.data();
  if(existing.userId!==user.uid){
    throw new Error("You are not authorized to edit this task.");
  }

  const cleanedUpdates={ ...updates, updatedAt:serverTimestamp() };

  if ("recurrence" in cleanedUpdates) {
        cleanedUpdates.isRecurringOriginal = cleanedUpdates.recurrence.type !== "none";
    }

  // keep title trimmed if changed
  if("title" in cleanedUpdates && typeof cleanedUpdates.title==="string"){
    cleanedUpdates.title=cleanedUpdates.title.trim();
  }
  
  // Apply completedAt logic
  const finalData=applyCompletedAtLogic(existing,cleanedUpdates);

  await updateDoc(ref,finalData);
  return { message:"Task updated successfully" };
}

/**
 * Delete a task
 * (replaces DELETE /api/tasks/:id)
 */
export async function deleteTask(id){
  const user=auth.currentUser;
  if(!user) throw new Error("Not authenticated");

  const ref=doc(db,"tasks",id);
  const snap=await getDoc(ref);

  if(!snap.exists()){
    throw new Error("Task not found or you are not authorized to delete it.");
  }

  const existing=snap.data();
  if(existing.userId!==user.uid){
    throw new Error("You are not authorized to delete this task.");
  }

  await deleteDoc(ref);
  return { message:"Task deleted successfully" };
}

/**
 * Unassign group from tasks when group is deleted
 * (replaces Task.updateMany({ group: id }, { $unset: { group: "" } }))
 */
export async function unassignTasksFromGroup(groupId){
  const user=auth.currentUser;
  if(!user) throw new Error("Not authenticated");

  const q=query(
    tasksCol,
    where("userId","==",user.uid),
    where("group","==",groupId)
  );
  const snap=await getDocs(q);

  const promises=[];
  snap.forEach(taskDoc=>{
    const ref=taskDoc.ref;
    promises.push(updateDoc(ref,{
      group:"",
      updatedAt:serverTimestamp()
    }));
  });

  await Promise.all(promises);
}
